import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
} from '@mui/material';

const ActivityDailyModal = ({ open, onClose, selectedDate, events = [] }) => {
    // 현재 선택된 활성화 탭(담당자명) 상태 관리
    const [activeTab, setActiveTab] = useState('');

    // 🚀 가로 탭 바의 실제 가로축 위치를 직접 제어하기 위한 useRef 선언
    const tabContainerRef = useRef(null);

    // 1️⃣ 선택된 날짜에 걸쳐 있는 일정들만 필터링 (시작일과 종료일 범위 체크)
    const dailyEvents = events.filter(event => {
        if (!event.start || !event.end) return false;
        const targetDate = selectedDate; // 'YYYY-MM-DD'
        const startDate = event.start.substring(0, 10);
        const endDate = event.end.substring(0, 10);
        return targetDate >= startDate && targetDate <= endDate;
    });

    // 2️⃣ 담당자(usrNm)별로 데이터 그룹화 처리
    const groupedByManager = dailyEvents.reduce((acc, event) => {
        const managerName = event.extendedProps?.usrNm || '담당자 미지정';
        if (!acc[managerName]) {
            acc[managerName] = [];
        }
        acc[managerName].push(event);
        return acc;
    }, {});

    const managers = Object.keys(groupedByManager);

    // 모달이 열릴 때마다 첫 번째 담당자가 자동으로 선택되도록 초기화
    useEffect(() => {
        if (open && managers.length > 0) {
            setActiveTab(managers[0]); // 첫 대상자 자동 선택 매핑 완비
        }
    }, [open, selectedDate, events]);

    // 🚀 [이동 작동 해결]: 네이티브 scrollLeft 수치를 추적하여 화살표 클릭 시 강제 가로 무빙 구현
    // 🚀 [최종 해결 핵심]: 탭이 바뀌었을 때 화면에서 가려진 경우 그 위치를 찾아 자동 스크롤시킵니다.
    const handleManagerSwitch = (direction) => {
        const currentIdx = managers.indexOf(activeTab);
        if (currentIdx === -1) return;

        let nextIdx = 0;
        if (direction === 'left') {
            nextIdx = currentIdx === 0 ? managers.length - 1 : currentIdx - 1;
        } else {
            nextIdx = currentIdx === managers.length - 1 ? 0 : currentIdx + 1;
        }

        // 1. 먼저 activeTab 상태를 다음 담당자로 변경합니다.
        const nextManager = managers[nextIdx];
        setActiveTab(nextManager);

        // 2. 브라우저 메모리가 변경된 탭의 HTML 위치를 ID로 즉시 정밀 추적(Find)합니다.
        setTimeout(() => {
            // 아래 managers.map 부분에서 id={`tab-${manager}`} 구조로 명시해 줄 것입니다.
            const nextTabElement = document.getElementById(`tab-${nextManager}`);
            
            if (nextTabElement) {
                // 💫 가려진 탭이 화면 밖(우측/좌측 너머)에 있다면 부드럽게 화면 중앙(center)으로 당겨옵니다.
                nextTabElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center' // 🧱 핵심: 가로축 중앙 정렬로 포커스 이동
                });
            }
        }, 50); // React 렌더링 타이밍을 맞추기 위한 미세 안전 타임아웃 처리
    };


    // 날짜 포맷 변경 (예: 2026-09-10 ➔ 2026년 09월 10일)
    const formatTitleDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${year}년 ${month}월 ${day}일`;
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            {/* 상단 타이틀 구역 명칭 (검은색 테마 유지) */}
            <DialogTitle sx={{ fontWeight: 'bold', pb: 1.5, backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                    {formatTitleDate(selectedDate)} 기술 활동 기록
                </Typography>
            </DialogTitle>
            
            {/* 본문 높이 500px 고정 틀 형성 및 전체 겉틀 스크롤 방지 잠금 */}
            <DialogContent 
                dividers 
                sx={{ 
                    backgroundColor: '#fcfcfc', 
                    p: 2, 
                    height: '500px',       
                    minHeight: '500px',    
                    maxHeight: '60vh',     
                    overflowY: 'hidden', 
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {dailyEvents.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center', m: 'auto' }}>
                        <Typography variant="body1" sx={{ color: 'rgba(0, 0, 0, 0.48)' }}>
                            해당 일자에 등록된 기술 활동 기록이 없습니다.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        
                        {/* 🚀 상단 고정 영역: 담당자 선택 책갈피 탭 바 및 삼각형 화살표 그룹 */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 2.5, 
                                borderBottom: '2px solid #e2e8f0', 
                                pb: '1px',
                                pl: '2px', 
                                flexShrink: 0,
                                position: 'relative',
                                justifyContent: 'space-between'
                            }}
                        >
                            {/* 🚀 [이동 가두기 고정 해결]: width 상한선(calc)을 줘서 스크롤 영역 활성화 */}
                            <Box 
                                ref={tabContainerRef}
                                sx={{ 
                                    display: 'flex', 
                                    gap: '6px', 
                                    overflowX: 'auto', 
                                    width: 'calc(100% - 65px)', // 우측 버튼 공간을 제외한 크기로 제한
                                    position: 'relative',
                                    top: '2px', 
                                    whiteSpace: 'nowrap',
                                    '&::-webkit-scrollbar': { display: 'none' }, // 지저분한 기본 스크롤바 숨김
                                    msOverflowStyle: 'none',  
                                    scrollbarWidth: 'none'    
                                }}
                            >
                                {managers.map((manager) => {
                                    const isSelected = activeTab === manager;
                                    return (
                                        <Box
                                            key={manager}
                                            id={`tab-${manager}`}
                                            onClick={() => setActiveTab(manager)}
                                            sx={{
                                                padding: '6px 14px',
                                                fontSize: '0.85rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                borderRadius: '4px 4px 0 0', 
                                                transition: 'all 0.15s ease-in-out',
                                                display: 'inline-block', 
                                                flexShrink: 0, 
                                                
                                                backgroundColor: isSelected ? '#ffffff' : 'transparent',
                                                color: isSelected ? '#1e293b' : '#64748b',
                                                border: isSelected ? '2px solid #e2e8f0' : '2px solid transparent',
                                                borderBottom: isSelected ? '2px solid #ffffff' : 'transparent',
                                                
                                                '&:hover': {
                                                    color: '#1e293b',
                                                    backgroundColor: isSelected ? '#ffffff' : '#f1f5f9'
                                                }
                                            }}
                                        >
                                            {manager}
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* 🚀 우측 삼각형 화살표 두 개 결합 모음 버튼 구역 */}
                            <Box sx={{ display: 'flex', gap: '4px', flexShrink: 0, position: 'relative', top: '-2px' }}>
                                {/* 🛠️ 왼쪽 이동 삼각형 단추 */}
                                <Button
                                    onClick={() => handleManagerSwitch('left')}
                                    sx={{
                                        minWidth: '24px', width: '24px', height: '24px', p: 0,
                                        color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '4px',
                                        backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        '&:hover': { backgroundColor: '#f1f5f9', color: '#1e293b' }
                                    }}
                                >
                                    {/* pointerEvents: 'none'으로 아이콘 레이어의 클릭 피드백 간섭 완벽 방지 */}
                                    <svg xmlns="http://w3.org" width="8" height="8" viewBox="0 0 24 24" fill="currentColor" style={{ pointerEvents: 'none' }}>
                                        <path d="M16 22L4 12L16 2z"/>
                                    </svg>
                                </Button>

                                {/* 🛠️ 오른쪽 이동 삼각형 단추 */}
                                <Button
                                    onClick={() => handleManagerSwitch('right')}
                                    sx={{
                                        minWidth: '24px', width: '24px', height: '24px', p: 0,
                                        color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '4px',
                                        backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        '&:hover': { backgroundColor: '#f1f5f9', color: '#1e293b' }
                                    }}
                                >
                                    {/* pointerEvents: 'none'으로 아이콘 레이어의 클릭 피드백 간섭 완벽 방지 */}
                                    <svg xmlns="http://w3.org" width="8" height="8" viewBox="0 0 24 24" fill="currentColor" style={{ pointerEvents: 'none' }}>
                                        <path d="M8 2l12 10L8 22z"/>
                                    </svg>
                                </Button>
                            </Box>
                        </Box>
                        {/* 🚀 하단 독립 스크롤 영역: 내용 카드 부분만 스크롤 구동 */}
                        <Box 
                            sx={{ 
                                flex: 1, 
                                overflowY: 'auto', 
                                pr: '4px', 
                                '&::-webkit-scrollbar': { width: '6px' }, 
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '3px' }
                            }}
                        >
                            {activeTab && groupedByManager[activeTab] && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 1 }}>
                                    {/* 🚀 [수정 핵심]: slice()로 원본 배열 복사 후, 시작 일시(start) 문자열을 비교하여 빠른 시간 순서대로 정렬(Sort)합니다. */}
                                    {activeTab && groupedByManager[activeTab] && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 1 }}>
                                            {groupedByManager[activeTab]
                                                .slice() // 원본 데이터 오염 방지를 위한 복사
                                                .sort((a, b) => {
                                                    const aTime = a.start || '';
                                                    const bTime = b.start || '';
                                                    return aTime.localeCompare(bTime); // ⏰ 가나다/숫자 순서(오름차순) 정렬 규칙 적용
                                                })
                                                .map((event) => {
                                                    const startTime = event.start ? event.start.substring(11, 16) : '';
                                                    const endTime = event.end ? event.end.substring(11, 16) : '';
                                                    const place = event.extendedProps?.company || '장소 미지정';
                                                    const content = event.extendedProps?.content || '내용 없음';

                                                    return (
                                                        <Box 
                                                            key={event.id} 
                                                            sx={{ 
                                                                p: 2, 
                                                                backgroundColor: '#ffffff', 
                                                                borderRadius: '6px', 
                                                                border: '1px solid #e2e8f0',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                                                            }}
                                                        >
                                                            {/* ❶ 첫 줄: 제목 */}
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 0.8, fontSize: '0.8rem' }}>
                                                                제목: <Box component="span" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>{event.title}</Box>
                                                            </Typography>

                                                            {/* ❷ 둘째 줄: 장소 및 시간 */}
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 1.5, fontSize: '0.8rem' }}>
                                                                장소: <Box component="span" sx={{ fontWeight: 600, color: '#334155', mr: 2 }}>{place}</Box>
                                                                시간: <Box component="span" sx={{ fontWeight: 600, color: '#334155' }}>{startTime} ~ {endTime}</Box>
                                                            </Typography>
                                                            
                                                            <Divider sx={{ my: 1.5, borderStyle: 'solid', borderColor: '#f1f5f9' }} />

                                                            {/* ❸ 셋째 줄: 내용 박스 */}
                                                            <Box sx={{ mt: 1 }}>
                                                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 0.5, fontSize: '0.8rem' }}>
                                                                    내용:
                                                                </Typography>
                                                                <Box sx={{ p: 1.5, backgroundColor: '#f8fafc', borderRadius: '4px', border: '1px solid #f1f5f9' }}>
                                                                    <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                                                                        {content}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                        </Box>
                                    )}

                                </Box>
                            )}
                        </Box>
                        
                    </Box>
                )}
            </DialogContent>
            
            {/* 하단 액션 닫기 바 */}
            <DialogActions sx={{ px: 3, py: 1.5, backgroundColor: '#f9f9f9', borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={onClose} color="primary" variant="contained" size="small" sx={{ fontWeight: 'bold', px: 2.5 }}>
                    닫기
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ActivityDailyModal;
